/**
 * Broker Account Settings Page (Web)
 * Settings page for managing broker account credentials and authentication
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Stack,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  AutoAwesome as AutoAwesomeIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { AuthenticatedLayout } from '../../components/layout/AuthenticatedLayout';
import { useBrokerAccounts } from '@shared/features/broker-accounts';
import { UpdateTOTPCredentialsForm } from './UpdateTOTPCredentialsForm';
import { MigrateToTOTPForm } from './MigrateToTOTPForm';

export function BrokerAccountSettingsPage() {
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();
  const { accounts, loading, loadAccounts } = useBrokerAccounts();
  const [showUpdateCredentials, setShowUpdateCredentials] = useState(false);
  const [showMigrateToTOTP, setShowMigrateToTOTP] = useState(false);

  useEffect(() => {
    if (accountId && accounts.length === 0 && !loading) {
      loadAccounts();
    }
  }, [accountId, accounts.length, loading, loadAccounts]);

  const account = accountId ? accounts.find((acc) => acc.id === accountId) : null;

  const handleUpdateCredentialsSuccess = async () => {
    setShowUpdateCredentials(false);
    await loadAccounts();
  };

  const handleMigrateToTOTPSuccess = async () => {
    setShowMigrateToTOTP(false);
    await loadAccounts();
  };

  if (loading && !account) {
    return (
      <AuthenticatedLayout>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
            <CircularProgress />
          </Box>
        </Container>
      </AuthenticatedLayout>
    );
  }

  if (!account) {
    return (
      <AuthenticatedLayout>
        <Container maxWidth="lg">
          <Box sx={{ mt: 4 }}>
            <Alert severity="error">Account not found</Alert>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/broker-accounts')}
              sx={{ mt: 2 }}
            >
              Back to Accounts
            </Button>
          </Box>
        </Container>
      </AuthenticatedLayout>
    );
  }

  const isTOTPEnabled = account.auth_mode === 'totp_automated' || account.totp_enabled;

  return (
    <AuthenticatedLayout>
      <Container maxWidth="lg">
        <Box sx={{ mt: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/broker-accounts')}
            sx={{ mb: 3 }}
          >
            Back to Accounts
          </Button>

          <Typography variant="h4" component="h1" gutterBottom>
            {account.account_name || 'Broker Account'} Settings
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Manage authentication settings and credentials for your broker account
          </Typography>

          {/* Account Info Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Account Information
              </Typography>
              <Stack spacing={1} sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Broker:
                  </Typography>
                  <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                    {account.broker_name}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Account ID:
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {account.broker_account_id}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Authentication Mode:
                  </Typography>
                  <Chip
                    label={isTOTPEnabled ? 'Automated (TOTP)' : 'Manual (OAuth)'}
                    color={isTOTPEnabled ? 'success' : 'default'}
                    size="small"
                    icon={isTOTPEnabled ? <AutoAwesomeIcon /> : <LockIcon />}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Status:
                  </Typography>
                  <Chip
                    label={account.active ? 'Connected' : 'Disconnected'}
                    color={account.active ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Authentication Settings Card */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Authentication Settings
              </Typography>

              {isTOTPEnabled ? (
                <>
                  <Alert severity="success" icon={<AutoAwesomeIcon />} sx={{ mb: 3 }}>
                    <Typography variant="body2">
                      <strong>Automated Login Enabled:</strong> Your account is configured for automated reconnection using TOTP credentials.
                    </Typography>
                  </Alert>

                  <Button
                    variant="outlined"
                    startIcon={<LockIcon />}
                    onClick={() => setShowUpdateCredentials(true)}
                    sx={{ mb: 2 }}
                  >
                    Update TOTP Credentials
                  </Button>
                </>
              ) : (
                <>
                  <Alert severity="info" icon={<LockIcon />} sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Manual Login:</strong> Your account uses OAuth authentication. You need to manually reconnect when tokens expire.
                    </Typography>
                    <Typography variant="body2">
                      Migrate to automated login to enable hands-off reconnection.
                    </Typography>
                  </Alert>

                  {account.broker_name === 'zerodha' && (
                    <Button
                      variant="contained"
                      startIcon={<AutoAwesomeIcon />}
                      onClick={() => setShowMigrateToTOTP(true)}
                      sx={{ mb: 2 }}
                    >
                      Migrate to Automated Login
                    </Button>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Update Credentials Dialog */}
          {showUpdateCredentials && (
            <UpdateTOTPCredentialsForm
              registrationId={account.id}
              accountName={account.account_name}
              brokerName={account.broker_name}
              onSuccess={handleUpdateCredentialsSuccess}
              onCancel={() => setShowUpdateCredentials(false)}
            />
          )}

          {/* Migrate to TOTP Dialog */}
          {showMigrateToTOTP && (
            <MigrateToTOTPForm
              registrationId={account.id}
              accountName={account.account_name}
              brokerName={account.broker_name}
              onSuccess={handleMigrateToTOTPSuccess}
              onCancel={() => setShowMigrateToTOTP(false)}
            />
          )}
        </Box>
      </Container>
    </AuthenticatedLayout>
  );
}

